<template>
    <section class="flex flex-col gap-5">
        <div v-if="loading" class="scaffold-body-small text-valence-stone-500">Loading…</div>
        <BaseNotification v-else-if="loadError" theme="error" :text="loadError" hide-dismiss />

        <template v-else-if="cycle">
            <button type="button" class="scaffold-body-small text-valence-stone-500 hover:text-valence-stone-900 flex items-center gap-1 self-start" @click="emit('back')">
                <i class="bi bi-chevron-left" aria-hidden="true"></i> Back
            </button>

            <div class="flex items-start justify-between gap-4 flex-wrap">
                <div class="flex items-start gap-3 min-w-0">
                    <ReflectionIcon :kind="cycle.is_self_assessment_only ? 'self' : 'sixty'" size="md" class="mt-1" />
                    <div class="min-w-0 flex flex-col gap-1">
                        <h2 class="title-body text-valence-stone-900 leading-tight">{{ headerTitle }}</h2>
                        <p v-if="reportDate" class="scaffold-details-small text-valence-stone-500">{{ reportDate }}</p>
                    </div>
                </div>
                <BasePopover
                    v-if="showResponders"
                    as-child
                    show-arrow
                    align="end"
                    :side-offset="6"
                    content-class="w-[264px] rounded-xl! p-2 shadow-[0_10px_30px_rgba(0,0,0,0.14)]!">
                    <template #trigger>
                        <button type="button" class="flex items-center gap-3 shrink-0 group" @click="onViewResponders">
                            <span class="flex -space-x-2">
                                <span
                                    v-for="(initial, i) in raterAvatarInitials"
                                    :key="`ra-${i}`"
                                    translate="no"
                                    class="inline-flex items-center justify-center w-6 h-6 rounded-full scaffold-details-bold text-white ring-[1.5px] ring-white"
                                    :style="{ backgroundColor: raterAvatarColor(i) }">
                                    {{ initial }}
                                </span>
                            </span>
                            <span class="scaffold-body-small text-valence-stone-900 group-hover:text-valence-stone-500">View Responders</span>
                        </button>
                    </template>
                    <template #content>
                        <p class="scaffold-details-bold text-valence-stone-500 px-2 pt-1">Responders</p>
                        <p class="scaffold-details-small text-valence-stone-500 px-2 pb-1">{{ raterCount }} {{ raterCount === 1 ? "response" : "responses" }}</p>
                        <ul class="flex flex-col">
                            <li v-for="(entry, i) in responderRows" :key="entry.role" class="flex items-center gap-2 px-2 py-1.5">
                                <span
                                    translate="no"
                                    class="inline-flex items-center justify-center w-6 h-6 rounded-full scaffold-details-bold text-white shrink-0"
                                    :style="{ backgroundColor: raterAvatarColor(i) }">
                                    {{ entry.label.charAt(0).toUpperCase() }}
                                </span>
                                <span class="scaffold-body-small text-valence-stone-900">{{ entry.label }}</span>
                                <span class="scaffold-details-small text-valence-stone-500 ml-auto">{{ entry.count }}</span>
                            </li>
                        </ul>
                    </template>
                </BasePopover>
            </div>

            <p v-if="lede" class="scaffold-body-small text-valence-stone-500">{{ lede }}</p>

            <div v-if="report && viewerIsSubject" class="flex items-center gap-2 flex-wrap">
                <BaseButton theme="neutral" size="sm" :disabled="downloading" class="base-btn-square inline-flex items-center gap-2" @click="onDownloadPdf">
                    <i class="bi bi-download" aria-hidden="true"></i>
                    {{ downloading ? "Downloading…" : "Download PDF" }}
                </BaseButton>
                <BaseButton theme="primary" size="sm" :disabled="startingChat" class="base-btn-dark inline-flex items-center gap-2" @click="onChatWithNadia">
                    <i class="bi bi-stars" aria-hidden="true"></i>
                    {{ startingChat ? "Starting…" : "Chat with Nadia" }}
                </BaseButton>
            </div>

            <BaseCard v-if="!report" theme="muted" padding="lg" class="border-dashed border-valence-grey-300 text-center">
                <div v-if="polling" class="flex flex-col items-center gap-3">
                    <LoadingSpinner class="w-5 h-5 text-valence-grey-500" />
                    <p class="body-small text-valence-grey-750">Generating your report…</p>
                </div>
                <p v-else class="body-small text-valence-grey-750">Your report is being generated. Refresh in a moment.</p>
            </BaseCard>

            <template v-else>
                <ExecutiveSummarySection v-if="report.executive_summary" :summary="report.executive_summary" />

                <SuggestedGoalsSection v-if="showSuggestedGoals" :cycle-id="cycleId" @hidden="suggestedGoalsHidden = true" />

                <!-- LAB-988: self-only cycles get the new pink-bars + blue-histogram design.
                     360 cycles keep the existing shared components since they use
                     rater breakdowns, cuts, self-vs-others, and anonymity handling that
                     don't apply to a single self response. -->
                <template v-if="cycle.is_self_assessment_only">
                    <SelfCategoryScoresSection v-if="aggregates && hasCategoryScores" id="category-scores" :sections="aggregates.sections ?? []" :max-score="defaultMaxScore" />
                    <SelfQuestionByQuestionSection
                        v-if="aggregates"
                        :questions="questionByQuestionItems"
                        :max-score="defaultMaxScore"
                        :subject-first-name="cycle.subject_first_name" />
                </template>
                <template v-else>
                    <CategoryScoresSection
                        v-if="aggregates && hasCategoryScores"
                        id="category-scores"
                        :sections="aggregates.sections ?? []"
                        :self-assess-enabled="cycle.self_assess_enabled"
                        :is-self-assessment-only="cycle.is_self_assessment_only"
                        :subject-name="viewerIsSubject ? '' : subjectName"
                        :max-score="defaultMaxScore" />

                    <SelfVsOthersSection v-if="aggregates && hasSelfVsOthers" :questions="scoreQuestions" :subject-name="viewerIsSubject ? '' : subjectName" />

                    <QuestionByQuestionSection
                        v-if="aggregates"
                        :questions="questionByQuestionItems"
                        :sections="aggregates.sections ?? []"
                        :subject-first-name="cycle.subject_first_name"
                        :is-self-assessment-only="cycle.is_self_assessment_only"
                        :viewer-is-subject="viewerIsSubject"
                        :anonymity-threshold="aggregates.anonymity_threshold" />
                </template>

                <ThemesSection v-if="(report.themes?.length ?? 0) > 0 && !cycle.is_self_assessment_only" :themes="report.themes" class="mt-2.5" />
            </template>
        </template>
    </section>
</template>

<script setup lang="ts">
import { useTimeoutPoll } from "@vueuse/core";
import { fetchApi, useFetch } from "~vue/api";
import CategoryScoresSection from "~vue/AssessmentResults/CategoryScoresSection.vue";
import ExecutiveSummarySection from "~vue/AssessmentResults/ExecutiveSummarySection.vue";
import QuestionByQuestionSection from "~vue/AssessmentResults/QuestionByQuestionSection.vue";
import SelfVsOthersSection from "~vue/AssessmentResults/SelfVsOthersSection.vue";
import SuggestedGoalsSection from "~vue/AssessmentResults/SuggestedGoalsSection.vue";
import ThemesSection from "~vue/AssessmentResults/ThemesSection.vue";
import type { Aggregates, CycleView, PerQuestion, Report } from "~vue/AssessmentResults/types";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BasePopover from "~vue/components/BasePopover.vue";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import ReflectionIcon from "~vue/pages/reflections/ReflectionIcon.vue";
import SelfCategoryScoresSection from "~vue/pages/reflections/SelfCategoryScoresSection.vue";
import SelfQuestionByQuestionSection from "~vue/pages/reflections/SelfQuestionByQuestionSection.vue";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { PERSPECTIVE_COLORS } from "~vue/utils/perspectivePalette";
import { DEFAULT_RATER_RELATIONSHIPS, formatDate } from "~vue/utils/threeSixty";
import { computed, onMounted, onUnmounted, ref } from "vue";

interface Props {
    cycleId: string;
    showSuggestedGoals?: boolean;
}

const { cycleId, showSuggestedGoals: showSuggestedGoalsProp = false } = defineProps<Props>();

const emit = defineEmits<{ (event: "back"): void }>();

const cycle = ref<CycleView | null>(null);
const report = ref<Report | null>(null);
const viewerIsSubject = ref(true);
const subjectName = computed(() => cycle.value?.subject_first_name ?? "");
const loading = ref(true);
const loadError = ref<string | null>(null);
const suggestedGoalsHidden = ref(false);
const showSuggestedGoals = computed(() => showSuggestedGoalsProp && !suggestedGoalsHidden.value && viewerIsSubject.value);

const aggregates = computed<Aggregates | null>(() => report.value?.aggregates ?? null);
const scoreQuestions = computed<PerQuestion[]>(() => aggregates.value?.per_question.filter((q) => q.is_score) ?? []);
const questionByQuestionItems = computed<PerQuestion[]>(() => aggregates.value?.per_question.filter((q) => q.is_score || q.open_text_synthesis) ?? []);
const hasSelfVsOthers = computed(() => scoreQuestions.value.some((q) => q.self_score !== null && q.all_avg !== null));
const hasCategoryScores = computed(() => (aggregates.value?.sections ?? []).some((s) => s.avg !== null));

const headerTitle = computed(() => {
    if (!cycle.value) {
        return "";
    }
    if (cycle.value.is_self_assessment_only) {
        return "Self Assessment";
    }
    return viewerIsSubject.value ? "360 Report" : `${subjectName.value}'s 360 Report`;
});

const lede = computed(() => {
    if (!cycle.value) {
        return "";
    }
    if (cycle.value.is_self_assessment_only) {
        return "A snapshot of how you rated yourself this mid-year — the themes in your answers, and where to focus next.";
    }
    return "";
});

const reportDate = computed(() => formatDate(report.value?.generated_at ?? cycle.value?.unlocked_at ?? null, "long"));

const RATER_AVATAR_MAX = 6;
const raterCount = computed(() => Object.values(aggregates.value?.rater_breakdown ?? {}).reduce((a, b) => a + b, 0));
const raterAvatarInitials = computed<string[]>(() => Array.from({ length: Math.min(raterCount.value, RATER_AVATAR_MAX) }, (_v, i) => String.fromCharCode(65 + i)));

// Rater identities are never in the report payload, so the popover lists the
// relationship mix instead of names.
const builtinRelationshipLabels = Object.fromEntries(DEFAULT_RATER_RELATIONSHIPS.map((option) => [option.value, option.label]));
const relationshipLabels = computed(() => aggregates.value?.relationship_labels ?? builtinRelationshipLabels);
const responderRows = computed(() =>
    Object.entries(aggregates.value?.rater_breakdown ?? {}).map(([role, count]) => ({ role, label: relationshipLabels.value[role] ?? role, count })),
);

// The relationship mix is still rater status: a subject who invited one manager
// reads "manager: 1" as that manager having responded. An organization that
// anonymizes rater status drops the responder cluster entirely.
const showResponders = computed(() => cycle.value !== null && !cycle.value.is_self_assessment_only && !cycle.value.rater_status_hidden && raterAvatarInitials.value.length > 0);

function raterAvatarColor(i: number): string {
    return PERSPECTIVE_COLORS[i % PERSPECTIVE_COLORS.length];
}

function onViewResponders() {
    void logUserInteraction(AmplitudeEventNames.ASSESSMENT_RESULTS_VIEWED, {
        cycle_id: cycleId,
        action: "view_responders_toggle",
    });
}

const defaultMaxScore = computed(() => {
    const first = scoreQuestions.value[0];
    return first?.max_score ?? first?.distribution.length ?? 5;
});

const startingChat = ref(false);
const downloading = ref(false);

let pollStartedAt = 0;
const POLL_TIMEOUT_MS = 60_000;

const {
    resume: resumePoll,
    pause: stopPolling,
    isActive: polling,
} = useTimeoutPoll(
    async () => {
        if (Date.now() - pollStartedAt > POLL_TIMEOUT_MS) {
            stopPolling();
            return;
        }
        const { data } = await useFetch(`/v1/reflections/cycles/${cycleId}/results`).json();
        const payload = data.value as { cycle: CycleView; report: Report | null; viewer_is_subject?: boolean } | null;
        if (payload?.report) {
            cycle.value = payload.cycle;
            report.value = payload.report;
            viewerIsSubject.value = payload.viewer_is_subject ?? true;
            stopPolling();
        }
    },
    3000,
    { immediate: false },
);

// Stop the poll on unmount so we don't keep hitting /results in the
// background after the user navigates back to the hub list.
onUnmounted(() => stopPolling());

async function onDownloadPdf() {
    if (downloading.value) {
        return;
    }
    void logUserInteraction(AmplitudeEventNames.ASSESSMENT_RESULTS_PDF_DOWNLOAD_CLICKED, { cycle_id: cycleId });
    downloading.value = true;
    try {
        const response = await fetchApi(`/v1/reflections/cycles/${cycleId}/export-pdf`);
        if (!response.ok) {
            return;
        }
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        const disposition = response.headers.get("Content-Disposition");
        const match = disposition?.match(/filename="?([^"]+)"?/);
        a.download = match?.[1] ?? (cycle.value?.is_self_assessment_only ? "self_assessment_report.pdf" : "360_report.pdf");
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    } finally {
        downloading.value = false;
    }
}

async function onChatWithNadia() {
    if (startingChat.value) {
        return;
    }
    void logUserInteraction(AmplitudeEventNames.ASSESSMENT_RESULTS_CHAT_WITH_NADIA_CLICKED, { cycle_id: cycleId });
    startingChat.value = true;
    try {
        const { data } = await useFetch(`/v1/reflections/cycles/${cycleId}/debrief-chat`).post().json();
        const chatUrl = (data.value as { chat_url?: string })?.chat_url;
        if (chatUrl) {
            globalThis.location.href = chatUrl;
        }
    } finally {
        startingChat.value = false;
    }
}

onMounted(async () => {
    const { data, error: fetchError, response } = await useFetch(`/v1/reflections/cycles/${cycleId}/results`).json();
    loading.value = false;
    if (fetchError.value) {
        if (response.value?.status === 400) {
            // Cycle not yet unlocked — bounce back to the hub list.
            emit("back");
            return;
        }
        loadError.value = (data.value as { error?: string })?.error || "Could not load results.";
        return;
    }
    const payload = data.value as { cycle: CycleView; report: Report | null; viewer_is_subject?: boolean };
    cycle.value = payload.cycle;
    report.value = payload.report;
    viewerIsSubject.value = payload.viewer_is_subject ?? true;
    const ratersByRole = payload.report?.aggregates?.rater_breakdown ?? {};
    const raterCount = Object.values(ratersByRole).reduce((a, b) => a + b, 0);
    void logUserInteraction(AmplitudeEventNames.ASSESSMENT_RESULTS_VIEWED, {
        cycle_id: cycleId,
        report_ready: payload.report !== null,
        rater_count: raterCount,
        has_self_assess: payload.cycle.self_assess_enabled,
    });

    if (!payload.report) {
        pollStartedAt = Date.now();
        resumePoll();
    }
});
</script>
